import { Box, Button, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { EditPencil, Eye, EyeClosed, SendDiagonal } from 'iconoir-react';
import type { ReactNode } from 'react';

type ComposerStep = 'compose' | 'confirm';

const CONTENT_X = { base: 4, md: 6 } as const;

interface EmailComposerCanvasProps {
  title?: string;
  step: ComposerStep;
  editLabel: string;
  previewLabel: string;
  sendLabel: string;
  confirmTitle: string;
  confirmDescription: string;
  confirmSendLabel: string;
  canProceed: boolean;
  isSending: boolean;
  isPreviewing: boolean;
  onEdit: () => void;
  onSendClick: () => void;
  onConfirmSend: () => void;
  onTogglePreview: () => void;
  cancelLabel?: string;
  onCancel?: () => void;
  warning?: ReactNode;
  senderSlot?: ReactNode;
  recipientSlot: ReactNode;
  programSlot?: ReactNode;
  subjectSlot: ReactNode;
  bodySlot: ReactNode;
  counterSlot?: ReactNode;
  /**
   * Wrap the composer in a bordered white "document" card. Off for the contact
   * drawer's inline outreach, where the composer fills the panel bare.
   */
  framed?: boolean;
}

function ComposerRow({ children }: { children: ReactNode }) {
  return (
    <Box py={1.5} borderBottom="1px solid" borderColor="border">
      <Box px={CONTENT_X}>{children}</Box>
    </Box>
  );
}

/**
 * Shared document-style shell for every email composer. It owns the visual
 * hierarchy only; send state, templates, variables and attachments stay with
 * the existing composer hook and child controls.
 */
export default function EmailComposerCanvas({
  title,
  step,
  editLabel,
  previewLabel,
  sendLabel,
  confirmTitle,
  confirmDescription,
  confirmSendLabel,
  canProceed,
  isSending,
  isPreviewing,
  onEdit,
  onSendClick,
  onConfirmSend,
  onTogglePreview,
  cancelLabel,
  onCancel,
  warning,
  senderSlot,
  recipientSlot,
  programSlot,
  subjectSlot,
  bodySlot,
  counterSlot,
  framed = true,
}: EmailComposerCanvasProps) {
  const isConfirming = step === 'confirm';

  return (
    <Box
      {...(framed
        ? {
            bg: 'white',
            border: '1px solid',
            borderColor: 'border',
            rounded: { base: 'xl', md: '2xl' },
          }
        : { bg: 'transparent' })}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      minH={0}
      h="full"
    >
      <HStack
        px={CONTENT_X}
        py={{ base: 3, md: 3.5 }}
        justify="space-between"
        gap={3}
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="border"
      >
        <HStack gap={3} minW={0}>
          {cancelLabel && onCancel ? (
            <Button
              variant="outline"
              colorPalette="gray"
              rounded="full"
              size="sm"
              px={3}
              onClick={onCancel}
              disabled={isSending}
            >
              {cancelLabel}
            </Button>
          ) : null}
          {isConfirming ? (
            <Button variant="outline" colorPalette="gray" rounded="full" size="sm" onClick={onEdit}>
              <EditPencil width={17} height={17} strokeWidth={1.9} />
              {editLabel}
            </Button>
          ) : null}
          {title ? (
            <Text fontSize="sm" color="fg.muted" fontWeight="medium" truncate>
              {title}
            </Text>
          ) : null}
        </HStack>

        <HStack gap={2} flexShrink={0}>
          <IconButton
            aria-label={previewLabel}
            variant={isPreviewing ? 'solid' : 'ghost'}
            colorPalette="gray"
            rounded="full"
            size="sm"
            onClick={onTogglePreview}
          >
            {isPreviewing ? (
              <EyeClosed width={18} height={18} strokeWidth={1.9} />
            ) : (
              <Eye width={18} height={18} strokeWidth={1.9} />
            )}
          </IconButton>
          <Button
            colorPalette="primary"
            rounded="full"
            size="sm"
            px={5}
            onClick={isConfirming ? onConfirmSend : onSendClick}
            disabled={!canProceed}
            loading={isSending}
          >
            <SendDiagonal width={18} height={18} strokeWidth={1.9} />
            {isConfirming ? confirmSendLabel : sendLabel}
          </Button>
        </HStack>
      </HStack>

      <VStack
        px={0}
        pb={{ base: 3, md: 4 }}
        align="stretch"
        gap={0}
        flex={1}
        minH={0}
        overflow="auto"
      >
        {warning ? (
          <Box px={CONTENT_X} mb={2.5}>
            {warning}
          </Box>
        ) : null}

        {isConfirming ? (
          <Box
            mx={CONTENT_X}
            mb={2.5}
            px={4}
            py={2.5}
            rounded="xl"
            bg="gray.50"
            border="1px solid"
            borderColor="border"
          >
            <Text fontWeight="semibold" fontSize="sm">
              {confirmTitle}
            </Text>
            <Text fontSize="xs" color="fg.muted" mt={0.5}>
              {confirmDescription}
            </Text>
          </Box>
        ) : null}

        {senderSlot ? <ComposerRow>{senderSlot}</ComposerRow> : null}
        <ComposerRow>{recipientSlot}</ComposerRow>
        {programSlot ? <ComposerRow>{programSlot}</ComposerRow> : null}
        <ComposerRow>{subjectSlot}</ComposerRow>
        <Box
          flex={1}
          minH={{ base: '280px', md: '340px' }}
          display="flex"
          flexDirection="column"
          px={CONTENT_X}
        >
          {bodySlot}
        </Box>
        {counterSlot ? (
          <Box px={CONTENT_X} pt={1.5} flexShrink={0}>
            {counterSlot}
          </Box>
        ) : null}
      </VStack>
    </Box>
  );
}
